import React, { useEffect, useState } from "react";
import { HiDownload } from "react-icons/hi";
import { toast } from "react-toastify";
import ApiService from "../../services/ApiService";
import Paginator from "../../components/Paginator";
import NoDataAnimation from "../../assets/animations/No Result Green theme.json"
import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";
import Image from "../../components/Image";

const EarningsCard = ({ item }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  function formatSingleDate(isoDate) {
    if (!isoDate) return "N/A";
    const date = new Date(isoDate);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    return `${day} ${month}`;
  }

  const isPending = item.amount_paid < 0 || !item.receipt;

  return (
    <div
      className="inter-font d-flex align-items-center border-bottom py-3 px-4"
      style={{
        minWidth: "850px",
        gap: "40px",
        justifyContent: "space-between",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      {/* Company Logo + Info */}
      <div
        className="d-flex align-items-center"
        style={{
          minWidth: "250px",
          flexDirection: isRTL ? "row-reverse" : "row",
          gap: "15px"
        }}
      >
        <Image
          src={item?.project_id?.company_id?.logo}
          className="rounded-circle"
          style={{
            width: "54px",
            height: "54px",
            objectFit: "cover"
          }}
          alt="Profile"
        />
        <div style={{
          textAlign: isRTL ? "right" : "left"
        }}>
          <h5 className="mb-1" style={{ fontSize: "16px", fontWeight: "600" }}>
            {item?.project_id?.title || "N/A"}
          </h5>
          <p className="mb-0" style={{ fontSize: "15px", color: "#777474" }}>
            {item?.project_id?.company_id?.company_name || "N/A"}
          </p>
        </div>
      </div>

      {/* Contract Type */}
      <div style={{
        minWidth: "100px",
        textAlign: isRTL ? "right" : "left"
      }}>
        <h6 style={{ fontSize: "15px", fontWeight: 600 }}>
          {t("t_d_text80")} {/* Contract Type */}
        </h6>
        <p style={{
          fontSize: "14px",
          color: "#777474",
        }}>
          {item?.job_type || "N/A"}
        </p>
      </div>

      {/* Payment Date */}
      <div style={{
        minWidth: "100px",
        textAlign: isRTL ? "right" : "left"
      }}>
        <h6 style={{ fontSize: "15px", fontWeight: 600 }}>
          {t("c_d_table_c4")} {/* Date */}
        </h6>
        <p style={{
          fontSize: "14px",
          color: "#777474",
          direction: "ltr",
          textAlign: isRTL ? "right" : "left"
        }}>
          {formatSingleDate(item?.payment_date)}
        </p>
      </div>

      {/* Amount */}
      <div style={{
        minWidth: "120px",
        textAlign: isRTL ? "right" : "left"
      }}>
        <h6 style={{ fontSize: "15px", fontWeight: 600 }}>
          {t('t_d_text85')} {/* Amount */}
        </h6>
        <p style={{
          fontSize: "14px",
          fontWeight: 500,
          color: isPending ? "#DD4223" : "rgba(54, 190, 92, 1)",
          direction: "ltr",
          unicodeBidi: "plaintext"
        }}>
          {isPending ? `- ${Math.abs(item.amount_paid)} AED` : `${item.amount_paid} AED`}
        </p>
      </div>

      {/* Invoice/Receipt Status */}
      <div style={{
        minWidth: "120px",
        textAlign: isRTL ? "right" : "left"
      }}>
        <h6 style={{ fontSize: "15px", fontWeight: 600 }}>
          {t("t_d_text93")} {/* Invoice */}
        </h6>
        {isPending ? (
          <span
            style={{
              color: "#CD496D",
              backgroundColor: "#FCE1E8",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              direction: "ltr",
              display: "inline-block"
            }}
          >
            Pending
          </span>
        ) : (
          <a
            href={item.receipt}
            target="_blank"
            rel="noreferrer"
            style={{
              color: "#5F8D73",
              backgroundColor: "#D3FDEA",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              textDecoration: "none",
              direction: "ltr"
            }}
          >
            <HiDownload size={16} /> Receipt
          </a>
        )}
      </div>
    </div>
  );
};

const Earnings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total_pages: 1,
    current_page: 1,
  });
  const [earningsData, setEarningsData] = useState([]);

  const fetchEarningsData = async (page = 1) => {
    try {
      setIsLoading(true);
      const res = await ApiService.request({
        method: "GET",
        url: `getEarnings?page=${page}`,
      });

      const data = res.data;
      if (data.status) {
        console.log("data -", data.data.earnings);
        setEarningsData(data.data.earnings);
        setPagination(data.data.pagination);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      console.error("Fetch Job Error:", err);
      toast.error("Failed to fetch jobs.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (
      newPage >= 1 &&
      newPage <= pagination.total_pages &&
      newPage !== pagination.current_page
    ) {
      fetchEarningsData(newPage);
    }
  };

  useEffect(() => {
    fetchEarningsData(1);
  }, []);

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div className="inter-font main-bg" style={{ minHeight: "100vh" }}>
      <div
        className="inter-font bg-white rounded-3 m-5 d-flex flex-column"
        style={{ minHeight: "79vh", overflow: "hidden" }}
      >
        <div
          className={`inter-font border-bottom py-3 ${isRTL ? 'me-4' : 'ms-4'}`}
          style={{
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left'
          }}
        >
          <h3
            className="inter-font pb-1"
            style={{
              fontSize: "20px",
              textAlign: isRTL ? 'right' : 'left'
            }}
          >
            {t("t_earnings_text")} {/* My Earnings */}
          </h3>
        </div>
        <div className="inter-font scrollable-list flex-grow-1" style={{
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}>

          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="inter-font spinner-border" role="status"></div>
            </div>
          ) : earningsData?.length === 0 ? (
            <div className="d-flex flex-column align-items-center py-5">
              <div style={{ width: 200 }}>
                <Lottie animationData={NoDataAnimation} loop={true} />
              </div>
            </div>
          ) : (
            earningsData.map((item, index) => (
              <EarningsCard key={index} item={item} />
            ))
          )}
        </div>

        {pagination?.total_pages > 1 && (
          <Paginator
            page={pagination.current_page}
            rows={pagination.per_page}
            totalRecords={pagination.total_records}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <style>{`
        .pagination-custom {
          display: flex;
          gap: 10px;
          align-items: center;
          justify-content: center;
          margin-top: 20px;
          padding: 0;
          list-style: none;
        }

        .pagination-custom .page-btn {
          background-color: transparent;
          border: none;
          font-size: 16px;
          color: #333;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          transition: 0.3s;
          cursor: pointer;
        }

        .pagination-custom .page-btn:hover {
          background-color: #f1f1f1;
        }

        .pagination-custom .page-btn.active {
          background-color: #e57c66;
          color: white;
          font-weight: bold;
        }

        .scrollable-list {
          max-height: 550px;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 10px;
        }

        .scrollable-list::-webkit-scrollbar {
          width: 6px;
        }

        .scrollable-list::-webkit-scrollbar-thumb {
          background-color: #c1c1c1;
          border-radius: 3px;
        }

        .scrollable-list::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default Earnings;
